import {useCallback, useState} from 'react';

import type {ConnectionSettingsResponse} from 'src/openapi/admin/models/ConnectionSettingsResponse';

export const SSO_STORAGE_PREFIX = 'sso-settings.';

export function readFromStorage<T>(key: string, fallback: T): T {
    try {
        const raw = window.sessionStorage.getItem(SSO_STORAGE_PREFIX + key);
        return raw != null ? JSON.parse(raw) as T : fallback;
    } catch {
        return fallback;
    }
}

export function hasWizardSessionState(): boolean {
    try {
        for (let i = 0; i < window.sessionStorage.length; i += 1) {
            const key = window.sessionStorage.key(i);
            if (key?.startsWith(SSO_STORAGE_PREFIX)) {
                return true;
            }
        }
    } catch {
        return false;
    }
    return false;
}

export function clearSsoSessionStorage(): void {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
            const key = window.sessionStorage.key(i);
            if (key?.startsWith(SSO_STORAGE_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => window.sessionStorage.removeItem(key));
    } catch {
        // sessionStorage unavailable
    }
}

export function loadConnectionToSessionStorage(connection: ConnectionSettingsResponse): void {
    const verifyDomainData = {
        domains: connection.domains.map(d => ({value: d, verified: true})),
    };

    const uploadMetadataData = {
        method: 'manual' as const,
        url: {url: '', loginUrl: '', certificate: '', valid: false},
        file: {fileName: null, content: null, loginUrl: '', certificate: '', valid: false},
        manual: {
            loginUrl: connection.ssoUrl ?? '',
            certificate: connection.certificate ?? '',
            valid: connection.ssoUrl != null,
        },
    };

    const reviewData = {
        isSsoMandatory: connection.ssoMandatory,
        saved: false,
    };

    window.sessionStorage.setItem(SSO_STORAGE_PREFIX + 'verifyDomain', JSON.stringify(verifyDomainData));
    window.sessionStorage.setItem(SSO_STORAGE_PREFIX + 'uploadMetadata', JSON.stringify(uploadMetadataData));
    window.sessionStorage.setItem(SSO_STORAGE_PREFIX + 'reviewAndEnable', JSON.stringify(reviewData));
    window.sessionStorage.setItem(SSO_STORAGE_PREFIX + 'confirmLoginFields', JSON.stringify(true));
}

export function useSsoSessionStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
    const [storedValue, setStoredValue] = useState<T>(() => readFromStorage(key, initialValue));

    const setValue = useCallback((value: T) => {
        setStoredValue(value);
        try {
            window.sessionStorage.setItem(SSO_STORAGE_PREFIX + key, JSON.stringify(value));
        } catch {
            // session storage full or unavailable — state still works in-memory
        }
    }, [key]);

    return [storedValue, setValue];
}
